'use client';

import { ReactComponent as ChevronRight } from '@assets/icons/chevron-right.svg';
import config from 'config';
import NextLink from 'next/link';

import { Box, Flex, Heading, Stack, useTheme, VStack } from '@chakra-ui/react';

import { NotFoundButtons } from 'types';

import { StaticMediaImage, Text } from 'ui';

const {
  notFound: { title, description, navigationLabel, bavigationButtons, image }
} = config;

/**
 * Sized on the app's own scale: the heading tops out at the dashboard page-title size, the
 * links read like the profile sidebar's list. Text and illustration sit side by side from
 * desktop up and stack (illustration first) below it.
 */
const NotFound = () => {
  const {
    colors: {
      palette: { notFound: styles }
    }
  } = useTheme();

  return (
    <Flex
      w='full'
      maxW='1100px'
      mx='auto'
      flex={1}
      direction={{ base: 'column', lg: 'row' }}
      align='center'
      justify='center'
      gap={{ base: '28px', md: '36px', lg: '60px' }}
      px={{ base: '20px', md: '40px' }}
      py={{ base: '40px', md: '60px', lg: '80px' }}
    >
      <Box
        order={{ base: 0, lg: 1 }}
        w={{ base: '100%', lg: '46%' }}
        maxW={{ base: '320px', md: '400px', lg: '480px' }}
        flexShrink={0}
      >
        <StaticMediaImage
          priority
          alt=''
          src={image}
          quality={100}
          style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
        />
      </Box>

      <VStack
        order={{ base: 1, lg: 0 }}
        flex={1}
        minW={0}
        maxW={{ base: '480px', lg: 'none' }}
        w='full'
        align={{ base: 'center', lg: 'flex-start' }}
        textAlign={{ base: 'center', lg: 'left' }}
        gap={0}
      >
        <Heading
          as='h1'
          fontSize={{ base: '22px', sm: '24px', md: '28px' }}
          lineHeight='130%'
          fontWeight={600}
          color={styles.title}
        >
          {title}
        </Heading>

        <Text
          mt={{ base: '10px', md: '14px' }}
          fontSize={{ base: '15px', md: '17px' }}
          lineHeight='150%'
          color={styles.description}
        >
          {description}
        </Text>

        <Text
          mt={{ base: '28px', md: '36px' }}
          mb='10px'
          fontSize='13px'
          lineHeight='140%'
          textTransform='uppercase'
          color={styles.description}
        >
          {navigationLabel}
        </Text>

        <Stack as='nav' w='full' maxW={{ lg: '360px' }} gap={0}>
          {bavigationButtons.map(({ link, label }: NotFoundButtons) => (
            <Flex
              key={label}
              as={NextLink}
              href={link}
              align='center'
              justify='space-between'
              gap='12px'
              py='14px'
              borderBottom='1px solid'
              borderColor='blackAlpha.200'
              color={styles.buttonLabel}
              fontSize={{ base: '14px', md: '15px' }}
              textTransform='uppercase'
              textAlign='left'
              transition='opacity .15s'
              _hover={{ opacity: 0.6 }}
            >
              <Text as='span'>{label}</Text>
              <ChevronRight
                aria-hidden
                width={8}
                height={14}
                fill='currentColor'
              />
            </Flex>
          ))}
        </Stack>
      </VStack>
    </Flex>
  );
};

export default NotFound;
